import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { fetchConsolidatedMis } from '../actions/consolidatedMisActions';

const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const now = new Date();
const CURRENT_MONTH = now.getMonth() + 1;
const CURRENT_YEAR = now.getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => CURRENT_YEAR - 2 + i);

const fmt = (val) => (val == null ? '—' : val);

const HEAD_CELL_SX = {
  fontWeight: 600,
  whiteSpace: 'nowrap',
  backgroundColor: '#f5f5f5',
  color: '#424242',
  fontSize: '0.72rem',
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  borderBottom: '2px solid #e0e0e0',
  py: 1.2,
};

const columns = [
  { id: 'sno',                   label: 'S.NO' },
  { id: 'supervisorName',        label: 'SUPERVISOR' },
  { id: 'bank',                  label: 'BANK' },
  { id: 'product',               label: 'PRODUCT' },
  { id: 'region',                label: 'REGION' },
  { id: 'provisionalBilling',    label: 'PROVISIONAL BILLING' },
  { id: 'billingRemarks',        label: 'BILLING REMARKS' },
  { id: 'totalBillRaised',       label: 'TOTAL BILL RAISED' },
  { id: 'tds',                   label: 'TDS%' },
  { id: 'billAmountAfterTDS',    label: 'BILL AMOUNT AFTER TDS' },
  { id: 'pendingBillsToBeRaised',label: 'PENDING BILLS TO BE RAISED' },
  { id: 'releasedPayout',        label: 'RELEASED PAYOUT' },
  { id: 'payoutsFromSup',        label: 'PAYOUTS FROM SUP' },
  { id: 'netSalaryRaised',       label: 'NET SALARY RAISED' },
  { id: 'payoutPlusSalary',      label: 'PAYOUT+SALARY' },
  { id: 'payout',                label: 'PAYOUT' },
  { id: 'salary',                label: 'SALARY' },
  { id: 'totalPercent',          label: 'TOTAL %' },
  { id: 'payoutSalaryStatus',    label: 'PAYOUT/SALARY STATUS' },
  { id: 'remarks',               label: 'REMARKS' },
  { id: 'date',                  label: 'DATE' },
  { id: 'paymentDate',           label: 'PAYMENT DATE' },
  { id: 'provisionalPayout',     label: 'PROVISIONAL PAYOUT' },
];

const ConsolidatedMis = () => {
  const dispatch = useDispatch();
  const role = useSelector((s) => s.getRoleInfo?.getRoleData || {});
  const [month, setMonth] = React.useState(CURRENT_MONTH);
  const [year, setYear] = React.useState(CURRENT_YEAR);

  const { loading, rows = [], error } = useSelector((s) => s.consolidatedMis);

  const loadData = useCallback(() => {
    dispatch(fetchConsolidatedMis(month, year));
  }, [dispatch, month, year]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const renderCell = (col, row) => {
    if (col.id === 'bank') return <Chip label={row.bank} size="small" sx={{ fontSize: '0.72rem', height: 22 }} />;
    if (col.id === 'product') return <Chip label={row.product} size="small" color="primary" variant="outlined" sx={{ fontSize: '0.72rem', height: 22 }} />;
    if (col.id === 'supervisorName') return <Typography variant="body2" fontWeight={500}>{row.supervisorName}</Typography>;
    if (col.id === 'tds' && row.tds != null) return `${row.tds}%`;
    if (col.id === 'totalPercent' && row.totalPercent != null) return `${row.totalPercent}%`;
    return fmt(row[col.id]);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={600} color="text.primary">
            Consolidated MIS
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {MONTH_NAMES[month - 1]} {year}
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={loadData} sx={{ color: 'text.secondary' }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel>Month</InputLabel>
          <Select value={month} label="Month" onChange={(e) => setMonth(e.target.value)}>
            {MONTH_NAMES.map((m, i) => (
              <MenuItem key={i + 1} value={i + 1}>{m}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 100 }}>
          <InputLabel>Year</InputLabel>
          <Select value={year} label="Year" onChange={(e) => setYear(e.target.value)}>
            {YEAR_OPTIONS.map((y) => (
              <MenuItem key={y} value={y}>{y}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress size={28} />
          </Box>
        ) : error ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="error">{error}</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell key={col.id} sx={HEAD_CELL_SX}>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      No data for {MONTH_NAMES[month - 1]} {year}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row) => (
                    <TableRow
                      key={row.sno}
                      hover
                      sx={{ '&:last-child td': { borderBottom: 0 } }}
                    >
                      {columns.map((col) => (
                        <TableCell
                          key={col.id}
                          sx={{ whiteSpace: 'nowrap', fontSize: '0.8rem', color: 'text.primary', py: 1 }}
                        >
                          {renderCell(col, row)}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default ConsolidatedMis;
